import React, { createContext } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import type { GetStaticProps } from 'next';
import { useRouter } from 'next/router';

import NotFoundPage from 'components/NotFoundPage';
import { client } from 'apolloClient/client';
import {
  NEIGHBORHOOD_AND_BLOGS_BY_SLUG,
  NEIGHBORHOOD_EXISTS_BY_SLUG,
  NEIGHBORHOOD_ITEMS_LIST,
} from 'apolloClient/queries/neighborhood';
import { useStaleSlugGuard } from 'hooks/useStaleSlugGuard';
import { Blog as BlogType, BlogsResponse } from 'apolloClient/types/Blog';
import { ApolloResponseWithSlug } from 'types';
import {
  Neighborhood,
  NeighborhoodResponse,
} from 'apolloClient/types/Neighborhood';
import { notfoundRedirect } from 'constants/notFoundParams';
import {
  flattenStrapiBulkDataItems,
  flattenStrapiDataItem,
} from 'lib/flattenStrapiBulkDataItems';
import {
  RequestUrl,
  StrapiBulkResponseData,
  StrapiResponse,
} from 'apolloClient/types';
import NeighborhoodPage from 'components/Neighborhood/NeighborhoodPage';
import { Entity, ResponseCollection } from 'apolloClient/types/common';
import { DEFAULT_REVALIDATE_INTERVAL, ORIGIN } from 'src/constants';
import { LOAD_HOME_PAGE_BLOGS } from 'apolloClient/queries/blogs';
import { queryWithRetry } from 'lib/queryWithRetry';

type NeighborhoodApolloResponse = ApolloQueryResult<
  ApolloResponseWithSlug<NeighborhoodResponse> & {
    blogs: StrapiResponse<StrapiBulkResponseData<BlogType>>;
  }
>;

export interface NeighborhoodPageContextInterface {
  neighborhood: Neighborhood;
  url: RequestUrl;
  globalBlogs: BlogType[];
}

export const NeighborhoodPageContext =
  createContext<NeighborhoodPageContextInterface | null>(null);

export interface NeighborhoodPageProps {
  neighborhood: NeighborhoodApolloResponse;
  globalBlogs: BlogType[];
  isPreview?: boolean;
}

const NeighborhoodComponent: React.FC<NeighborhoodPageProps> = ({
  neighborhood,
  globalBlogs,
  isPreview = false,
}) => {
  const { asPath, query } = useRouter();

  // If this neighborhood's slug changed, the old URL keeps serving this stale
  // cached page. Re-check existence client-side (by the URL slug) and 404.
  const urlSlug = typeof query.urlParam === 'string' ? query.urlParam : '';
  const isStalePage = useStaleSlugGuard<{ findSlug?: { data?: unknown } }>(
    NEIGHBORHOOD_EXISTS_BY_SLUG,
    { slug: urlSlug },
    (d) => Boolean(d?.findSlug?.data),
    isPreview
  );

  if (isStalePage) {
    return <NotFoundPage />;
  }

  const neighborhoodItem = neighborhood?.data?.findSlug?.data;

  if (!neighborhoodItem) {
    console.warn(
      '⚠️ Missing neighborhood data, redirecting to notFound:',
      asPath
    );
    return null;
  }

  const neighborhoodData = flattenStrapiDataItem(neighborhoodItem);
  const slug = neighborhoodData?.slug;

  if (!slug) {
    console.warn(
      '⚠️ Missing slug in neighborhoodData, redirecting to notFound'
    );
    return null;
  }

  return (
    <NeighborhoodPageContext.Provider
      value={{
        neighborhood: neighborhoodData,
        url: `${ORIGIN}${asPath}`,
        globalBlogs,
      }}
    >
      <NeighborhoodPage />
    </NeighborhoodPageContext.Provider>
  );
};

export default NeighborhoodComponent;

export const getStaticProps: GetStaticProps<
  { neighborhood?: NeighborhoodApolloResponse },
  { urlParam: string }
> = async ({ params }) => {
  const urlParam = params?.urlParam as string;

  const URL = `/${urlParam}`;
  const start = Date.now();
  console.log(`[NeighborhoodPage] getStaticProps started for ${URL}`);

  try {
    const [neighborhoodResult, blogsResult] = await Promise.allSettled([
      queryWithRetry<
        ApolloResponseWithSlug<NeighborhoodResponse> & {
          blogs: StrapiResponse<StrapiBulkResponseData<BlogType>>;
        }
      >(
        {
          query: NEIGHBORHOOD_AND_BLOGS_BY_SLUG,
          variables: {
            slug: urlParam,
            blogLimit: 3,
            neighborsLimit: 3,
            blogsDateFrom: new Date(
              new Date().setFullYear(new Date().getFullYear() - 2)
            ).toISOString(),
          },
          fetchPolicy: 'no-cache',
        },
        'NeighborhoodPage/neighborhood'
      ),
      queryWithRetry<{ blogs: BlogsResponse }>(
        { query: LOAD_HOME_PAGE_BLOGS },
        'NeighborhoodPage/blogs'
      ),
    ]);

    if (neighborhoodResult.status === 'rejected') {
      console.error(
        `[NeighborhoodPage] Neighborhood query failed for ${URL}:`,
        neighborhoodResult.reason?.message || neighborhoodResult.reason
      );
      throw neighborhoodResult.reason;
    }

    if (blogsResult.status === 'rejected') {
      console.error(
        `[NeighborhoodPage] Blogs query failed for ${URL}:`,
        blogsResult.reason?.message || blogsResult.reason
      );
      throw blogsResult.reason;
    }

    if (!neighborhoodResult.value?.data?.findSlug?.data) {
      console.warn(
        `[NeighborhoodPage] Neighborhood not found for slug: ${urlParam}`
      );
      return notfoundRedirect;
    }

    console.log(
      `[NeighborhoodPage] getStaticProps done in ${
        Date.now() - start
      }ms for ${URL}`
    );
    return {
      props: {
        neighborhood: neighborhoodResult.value,
        globalBlogs: flattenStrapiBulkDataItems(
          blogsResult.value?.data?.blogs?.data
        ),
      },
      revalidate: DEFAULT_REVALIDATE_INTERVAL,
    };
  } catch (error: any) {
    console.error(`[NeighborhoodPage] Error fetching data for ${URL}:`);

    if (error?.graphQLErrors?.length) {
      error.graphQLErrors.forEach((gqlError: any, index: number) => {
        console.error(
          `  GraphQL Error ${index + 1}:`,
          JSON.stringify(
            {
              message: gqlError.message,
              extensions: gqlError.extensions,
              path: gqlError.path,
              locations: gqlError.locations,
            },
            null,
            2
          )
        );
      });
    }

    if (error?.networkError) {
      console.error(
        '  Network Error:',
        JSON.stringify(
          {
            message: error.networkError.message,
            statusCode: error.networkError.statusCode,
            result: error.networkError.result,
          },
          null,
          2
        )
      );
    }

    if (!error?.graphQLErrors?.length && !error?.networkError) {
      console.error('  Error:', error?.message || error);
    }

    return notfoundRedirect;
  }
};

export async function getStaticPaths() {
  // We decided to not fetch them to reduce build time.
  // TODO: Enable static paths when revalidation will be fixed
  // const pageSize = 100;
  // let page = 1;
  // let finished = false;
  // let result: Entity<Neighborhood>[] = [];

  // while (!finished) {
  //   const { data } = await client.query<{
  //     neighborhoods: ResponseCollection<Neighborhood>;
  //   }>({
  //     query: NEIGHBORHOOD_ITEMS_LIST,
  //     variables: { pageSize, page },
  //     fetchPolicy: 'no-cache',
  //   });

  //   result = result.concat(data.neighborhoods.data);
  //   page += 1;
  //   if (data.neighborhoods.data.length < pageSize) {
  //     finished = true;
  //   }
  // }

  // const paths = result
  //   .filter((nh) => nh.attributes.slug)
  //   .map((nh) => ({
  //     params: { urlParam: nh.attributes.slug },
  //   }));

  return { paths: [], fallback: 'blocking' };
}
